import { JSX, useState } from "react";
import { ConfirmRemoveAlert } from "../Dialog";
import { DataTable } from "@/components/shared";
import { getColumnsPurchases } from "../Purchases/columns";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { PurchaseDetails } from "@/graphql/__generated__/graphql";
import { useRemovePurchaseMutationHook } from "@/hooks/mutations";
import AddPurchase from "./Form";
import { useFetchPurchasesOnTenderQueryHook, useFetchServicesOnTenderQueryHook } from "@/hooks/queries";

interface AddPurchaseComponentProps {
  /** Optional purchases data passed from parent (e.g., from a Service) */
  purchases?: PurchaseDetails[];
}

/**
 * Add Purchase component. Acts as a wrapper for the AddPurchase form.
 * Can display purchases from props (e.g., linked to a Service) or fetch from tender.
 *
 * @returns {JSX.Element}
 */
const AddPurchaseComponent = ({ purchases }: AddPurchaseComponentProps): JSX.Element => {

  // States
  const [showAddPurchaseModal, setShowAddPurchaseModal] = useState<boolean>(false);
  const [showConfirmRemoveAlert, setShowConfirmRemoveAlert] = useState<boolean>(false);
  const [currentPurchaseID, setCurrentPurchaseID] = useState<number | undefined>();

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["translation", "common", "components"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  // Mutation hooks
  const { removePurchaseMutation, loading: removePurchaseLoading } = useRemovePurchaseMutationHook();

  // Fetch purchases from tender if not provided via props
  const { data: purchasesData, loading: purchasesLoading } = useFetchPurchasesOnTenderQueryHook(Number(tenderID));
  const { data: servicesData } = useFetchServicesOnTenderQueryHook(Number(tenderID), false, true);

  // Use purchases from props if provided, otherwise use fetched data
  const displayPurchases = purchases ?? (purchasesData?.fetchPurchasesOnTender ?? []) as PurchaseDetails[];
  const services = servicesData?.fetchServicesOnTender?.services ?? [];

  /**
   * OnClick handler for the Remove button in the Purchases table
   * @param {PurchaseDetails} purchaseDetails - The current purchase details to be removed
   */
  const handleClickRemove = (purchaseDetails: PurchaseDetails) => {
    setCurrentPurchaseID(purchaseDetails.purchase.id);
    setShowConfirmRemoveAlert(true);
  };

  /**
   * Handler function for `removePurchaseMutation`
   */
  const handleRemovePurchase = () => {
    removePurchaseMutation({
      variables: {
        tenderId: Number(tenderID),
        purchaseId: Number(currentPurchaseID),
      }
    });
  };

  return (
    <div>
      <DataTable
        headerStyling="font-normal text-sm"
        disableRowColour={true}
        loading={!purchases && purchasesLoading}
        displayAmount={displayPurchases.length || 10}
        data={displayPurchases}
        columns={getColumnsPurchases(intl, t, false, () => {}, handleClickRemove, removePurchaseLoading, services)}
        className="max-h-120"
      />
      <AddPurchase open={showAddPurchaseModal} onOpenChange={setShowAddPurchaseModal}/>

      {/* Confirm Remove Purchase */}
      <ConfirmRemoveAlert
        open={showConfirmRemoveAlert}
        onOpenChange={setShowConfirmRemoveAlert}
        actionButtonFn={handleRemovePurchase}
        bodyText={t("components:alertdialogs.removepurchase")}
      />
    </div>
  );
};

export default AddPurchaseComponent;
